/**
 * Route guard that only allows providers to access dashboard routes.
 * Clients are redirected to /client-dashboard.
 * Unauthenticated users are redirected to /login.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface ProviderRouteProps {
  children: React.ReactNode;
}

export function ProviderRoute({ children }: ProviderRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();

  const { data: userType, isLoading: profileLoading } = useQuery({
    queryKey: ['user-type', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('profiles')
        .select('user_type')
        .eq('id', user.id)
        .maybeSingle();
      return data?.user_type || null;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  if (authLoading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    const redirectPath = location.pathname + location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  // Check metadata first (fast), then DB profile (authoritative)
  const metaType = user.user_metadata?.user_type;
  const resolvedType = userType || metaType;

  if (resolvedType === 'client') {
    return <Navigate to="/client-dashboard" replace />;
  }

  return <>{children}</>;
}
